/**
 * The door queue and the guest list (#126, #127): what the screens send,
 * and the guest page's own calls, which carry no sign-in at all.
 */

import type { GuestMenuItem, GuestPrefs, GuestRecord, GuestVisit, Party, VisitSource } from "@baros/core";
import { ApiError, request } from "../api.js";

/** What the door sees of a returning guest. */
export interface DoorGuest {
  readonly guestId: string;
  readonly name: string;
  readonly phone: string;
  readonly visits: number;
  readonly lastSeenAt: string;
  readonly lastSeenSource: VisitSource;
  readonly prefs: GuestPrefs;
}

export type QueueParty = Omit<Party, "linkToken"> & { readonly guest: DoorGuest | null };

export type ListedGuest = GuestRecord & { readonly phoneDisplay: string };

const v = (venueId: string) => `/v1/venues/${encodeURIComponent(venueId)}`;

export const queueApi = {
  list: (token: string, venueId: string) =>
    request<{ parties: QueueParty[]; texting: boolean; textingOff?: "plan" | "setup" | null }>(
      token,
      "GET",
      `${v(venueId)}/queue`,
    ),
  lookup: (token: string, venueId: string, phone: string) =>
    request<{ guest: DoorGuest | null }>(token, "GET", `${v(venueId)}/guests/lookup?phone=${encodeURIComponent(phone)}`),
  add: (token: string, venueId: string, body: { phone: string; name: string; size: number; note: string }) =>
    request<{ party: QueueParty }>(token, "POST", `${v(venueId)}/queue`, body),
  move: (token: string, venueId: string, partyId: string, to: Party["status"]) =>
    request<{ party: QueueParty }>(token, "POST", `${v(venueId)}/queue/${encodeURIComponent(partyId)}/move`, { to }),
  textAgain: (token: string, venueId: string, partyId: string) =>
    request<{ party: QueueParty }>(token, "POST", `${v(venueId)}/queue/${encodeURIComponent(partyId)}/text-again`, {}),
  logVisit: (token: string, venueId: string, guestId: string) =>
    request<{ visit: GuestVisit }>(token, "POST", `${v(venueId)}/guests/${encodeURIComponent(guestId)}/visits`, {}),
  undoVisit: (token: string, venueId: string, guestId: string, at: string) =>
    request<{ undone: string }>(
      token,
      "DELETE",
      `${v(venueId)}/guests/${encodeURIComponent(guestId)}/visits/${encodeURIComponent(at)}`,
    ),
};

export const guestsApi = {
  list: (token: string, q: string) =>
    request<{ guests: ListedGuest[]; total: number }>(token, "GET", `/v1/guests${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  get: (token: string, guestId: string) =>
    request<{ guest: ListedGuest; visits: GuestVisit[] }>(token, "GET", `/v1/guests/${encodeURIComponent(guestId)}`),
  update: (
    token: string,
    guestId: string,
    body: { name?: string; prefs?: GuestPrefs; email?: string | null; marketing?: boolean },
  ) => request<{ guest: ListedGuest }>(token, "PUT", `/v1/guests/${encodeURIComponent(guestId)}`, body),
  remove: (token: string, guestId: string) =>
    request<{ deleted: string }>(token, "DELETE", `/v1/guests/${encodeURIComponent(guestId)}`),
};

// ------------------------------------------------------------ guest page

export interface GuestPageData {
  readonly venue: { readonly name: string };
  readonly party: {
    readonly name: string;
    readonly size: number;
    readonly status: Party["status"];
    readonly ahead: number | null;
    readonly readyAt: string | null;
  };
  readonly menu: GuestMenuItem[];
  readonly showPrices: boolean;
  readonly saved: { readonly hasEmail: boolean; readonly marketing: boolean; readonly prefs: GuestPrefs } | null;
  readonly suggestions: string[];
}

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

/** A call from a stranger's phone: no Authorization, nothing but the token in the path. */
async function open<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body !== undefined ? { "content-type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, (await res.text().catch(() => "")) || `${res.status}`);
  return (await res.json()) as T;
}

const page = (token: string) => `/v1/public/guest/${encodeURIComponent(token)}`;

export const guestPageApi = {
  get: (token: string) => open<GuestPageData>("GET", page(token)),
  save: (token: string, body: { email?: string; marketing?: boolean; aromas?: string[]; styles?: string[] }) =>
    open<{ saved: boolean }>("POST", page(token), body),
  cancel: (token: string) => open<{ status: Party["status"] }>("POST", `${page(token)}/cancel`, {}),
};
